import { ICustomBlockUsedStyles } from "@kuika/kuika-cl-model";
import React, { Component, createRef } from "react";
import { BlockModel, BlocksService } from "../../../../../services/blocks-service";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSpinnerSmall from "../../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";

interface BlockItemProps {
  block: BlockModel;
  onUsedBlockStylesSet: (usedStyles: ICustomBlockUsedStyles, blockJson: string) => string;
  onDelete?: () => void;
}

interface BlockItemState {
  isLoading: boolean;
  hasError: boolean;
  screenshotUrl: string;
  isPressing: boolean;
  hasJsonLoaded: boolean;
  blockJson?: string;
  showHint: boolean;
  hintText?: string;
  imageLoaded: boolean;
}

class BlockItem extends Component<BlockItemProps, BlockItemState> {
  private rootRef = createRef<HTMLDivElement>();

  private io?: IntersectionObserver;

  private hasStartedLoading = false;

  constructor(props: BlockItemProps) {
    super(props);
    this.state = {
      isLoading: true,
      hasError: false,
      screenshotUrl: "",
      isPressing: false,
      hasJsonLoaded: false,
      blockJson: undefined,
      showHint: false,
      hintText: undefined,
      imageLoaded: false
    };
  }

  async componentDidMount() {
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasStartedLoading) {
            this.hasStartedLoading = true;
            Promise.all([this.loadScreenshot(), this.loadJson()]);
            if (this.io && this.rootRef.current) this.io.unobserve(this.rootRef.current);
          }
        });
      },
      { root: null, rootMargin: "200px 0px", threshold: 0.01 }
    );
    if (this.rootRef.current) this.io.observe(this.rootRef.current);
  }

  async componentDidUpdate(prevProps: BlockItemProps) {
    if (prevProps.block.id !== this.props.block.id) {
      this.hasStartedLoading = false;
      if (this.rootRef.current && this.io) this.io.observe(this.rootRef.current);
    }
  }

  componentWillUnmount(): void {
    if (this.io) {
      try {
        if (this.rootRef.current) this.io.unobserve(this.rootRef.current);
        this.io.disconnect();
      } catch {}
    }
  }

  private loadScreenshot = async () => {
    const { block } = this.props;
    try {
      this.setState({ isLoading: true, hasError: false, imageLoaded: false });
      const blobUrl = await BlocksService.GetBlockScreenshotFromList(block);
      this.setState({ screenshotUrl: blobUrl });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load screenshot for block:", block.id, error);
      this.setState({ hasError: true });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  private loadJson = async () => {
    try {
      const json = await BlocksService.GetBlockJson(this.props.block.id);
      this.setState({ hasJsonLoaded: true, blockJson: json });
    } catch {
      this.setState({ hasJsonLoaded: false });
    }
  };

  private handleMouseDown = () => {
    if (!this.state.hasJsonLoaded) {
      KMainFunctions.displayWarningNotification("Preparing block, please wait a moment.");
      return;
    }
    this.setState({ isPressing: true });
  };

  private handleMouseUp = () => {
    this.setState({ isPressing: false });
  };

  private handleDragEnd = () => {
    this.setState({ isPressing: false });
  };

  private handleDragStart = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!this.state.hasJsonLoaded) {
      event.preventDefault();
      event.stopPropagation();
      KMainFunctions.displayWarningNotification("Preparing block, please wait a moment.");
      return;
    }
    const isCustomBlock = this.props.block.category === "Custom";

    if (this.state.blockJson) {
      let format = "draggable-block";
      let blockJson = this.state.blockJson;
      if (isCustomBlock) {
        format = "draggable-custom-block";
        try {
          const { usedStyles, di } = JSON.parse(this.state.blockJson);
          blockJson = JSON.stringify(di);
          blockJson = this.props.onUsedBlockStylesSet(usedStyles as ICustomBlockUsedStyles, blockJson);
        } catch {}
      }
      event.dataTransfer.setData(format, blockJson);
    }
    const rect = this.rootRef.current?.getBoundingClientRect();
    const scale = 1.0;
    const buffer = document.getElementById("html-buffer");
    const preview = document.createElement("div");
    preview.style.position = "absolute";
    preview.style.top = "0";
    preview.style.left = "0";
    preview.style.zIndex = "-1";
    preview.style.borderRadius = "var(--radius-l)";
    preview.style.overflow = "hidden";
    preview.style.boxShadow = "var(--shadow-md)";
    preview.style.border = "1px solid var(--dark-3)";
    preview.style.background = "var(--dark-0)";
    preview.style.padding = "8px";

    if (rect) {
      preview.style.width = `${rect.width * scale}px`;
      preview.style.height = `${rect.height * scale}px`;
    } else {
      preview.style.width = "240px";
      preview.style.height = `${240 / (16 / 9)}px`;
    }

    const { screenshotUrl, hasError } = this.state;
    if (screenshotUrl && !hasError) {
      const img = document.createElement("img");
      img.src = screenshotUrl;
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.objectFit = "contain";
      img.style.background = "var(--dark-0)";
      img.style.borderRadius = "var(--radius-xs)";
      preview.appendChild(img);
    } else {
      const fallback = document.createElement("div");
      fallback.style.width = "100%";
      fallback.style.height = "100%";
      fallback.style.display = "flex";
      fallback.style.alignItems = "center";
      fallback.style.justifyContent = "center";
      fallback.style.background = "var(--dark-1)";
      fallback.style.color = "var(--dark-6)";
      fallback.style.fontWeight = "600";
      fallback.style.fontSize = "12px";
      fallback.style.borderRadius = "var(--radius-xs)";
      fallback.innerText = "Block";
      preview.appendChild(fallback);
    }

    if (buffer) {
      buffer.appendChild(preview);
      let offsetX = preview.clientWidth / 2;
      let offsetY = preview.clientHeight / 2;
      if (rect) {
        offsetX = (event.clientX - rect.left) * scale;
        offsetY = (event.clientY - rect.top) * scale;
      }
      event.dataTransfer.setDragImage(preview, offsetX, offsetY);
      setTimeout(() => {
        try {
          buffer.removeChild(preview);
        } catch {}
      }, 100);
    } else {
      let offsetX = preview.clientWidth / 2;
      let offsetY = preview.clientHeight / 2;
      if (rect) {
        offsetX = (event.clientX - rect.left) * scale;
        offsetY = (event.clientY - rect.top) * scale;
      }
      event.dataTransfer.setDragImage(preview, offsetX, offsetY);
      setTimeout(() => {
        try {
          preview.remove();
        } catch {}
      }, 100);
    }
    this.setState({ isPressing: false });
  };

  render() {
    const { isLoading, hasError, screenshotUrl, isPressing } = this.state;

    return (
      <div
        ref={this.rootRef}
        data-testid={`customBlock-${this.props.block.name}`}
        className="kuika-block-item"
        draggable
        onDragStart={this.handleDragStart}
        onMouseDown={this.handleMouseDown}
        onMouseUp={this.handleMouseUp}
        onDragEnd={this.handleDragEnd}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          // Check if the click is from a button inside KuikaDropdownMenu
          let el = e.target as HTMLElement | null;
          while (el) {
            if (el.tagName === "BUTTON" || el.closest(".kuika-button")) {
              return;
            }
            el = el.parentElement;
          }

          if (!this.state.hasJsonLoaded) {
            this.setState({ showHint: true, hintText: "Preparing block…" });
          } else {
            this.setState({
              showHint: true,
              hintText: "Drag to add this block"
            });
          }
          window.setTimeout(() => {
            this.setState({ showHint: false, hintText: undefined });
          }, 1800);
        }}
        style={{
          transform: isPressing ? "scale(1.06)" : "scale(1)",
          transformOrigin: "center center",
          transition: "transform 150ms ease, box-shadow 150ms ease",
          boxShadow: isPressing ? "var(--shadow-md)" : undefined,
          borderRadius: "var(--radius-l)",
          willChange: "transform"
        }}
      >
        {this.props.block.category === "Custom" && !isLoading && (
          <KuikaDropdownMenu
            options={[
              {
                icon: <KuikaSvg name="trash" width={16} height={16} color="var(--red-5)" />,
                label: "Delete",
                onClick: (e) => {
                  this.props.onDelete?.();
                },
                danger: true,
                dataTestId: "blockDeleteMenuItem"
              }
            ]}
          >
            <KuikaButton
              data-testid="blockOptionsButton"
              aria-label="Block options"
              style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
              type="tertiary"
              isIcon
            >
              <KuikaSvg name="dots" width={16} height={16} color="var(--dark-7)" />
            </KuikaButton>
          </KuikaDropdownMenu>
        )}
        {this.state.showHint && (
          <div role="status" aria-live="polite" className="kuika-block-item__hint">
            {this.state.hintText}
          </div>
        )}
        {isLoading ? (
          <div className="kuika-block-item__loading" style={{ aspectRatio: "16/9" }}>
            <KuikaSpinnerSmall size="small" />
          </div>
        ) : hasError ? (
          <div className="kuika-block-item__error" style={{ aspectRatio: "16/9" }}>
            <KuikaSvg name="image-off" width={24} height={24} />
          </div>
        ) : (
          <img
            data-testid="blockPreviewImage"
            src={screenshotUrl}
            alt={`Block ${this.props.block.id}`}
            className={`kuika-block-item__image${this.state.imageLoaded ? " is-visible" : ""}`}
            onError={() => this.setState({ hasError: true })}
            onLoad={() => {
              const show = () => this.setState({ imageLoaded: true });
              if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(() => requestAnimationFrame(show));
              } else {
                setTimeout(show, 0);
              }
            }}
            style={{ aspectRatio: "16/9" }}
          />
        )}
      </div>
    );
  }
}

export default BlockItem;
